import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { FilteredResults } from '../components/filters/FilteredResults';
import { Seo } from '../components/Seo';
import { breadcrumb } from '../lib/structuredData';
import { SCHEMAS } from '../lib/filterSchemas';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useApp } from '../context/AppContext';
import { ShoppingBag, Store as StoreIcon, Tag, Info } from 'lucide-react';

function ProductCard({ p, currency }) {
  return (
    <Link to={`/store/${p.slug}`} className="block" data-testid={`store-product-${p.slug}`}>
      <Card className="overflow-hidden rounded-2xl card-elevate h-full">
        <div className="aspect-[4/3] bg-secondary overflow-hidden relative">
          {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}
          {p.is_demo && <Badge variant="outline" className="absolute top-2 left-2 rounded-full bg-amber-500/15 text-amber-700 border-amber-500/30 text-[10px]">DEMO</Badge>}
          {p.featured && <Badge className="absolute top-2 right-2 rounded-full bg-accent text-accent-foreground text-[10px]">Featured</Badge>}
        </div>
        <div className="p-4">
          <p className="text-[11px] text-muted-foreground">{p.brand}</p>
          <p className="font-semibold text-sm leading-snug line-clamp-2">{p.name}</p>
          <div className="mt-2 flex items-end justify-between">
            <div>
              <p className="text-[10px] text-muted-foreground">Best price we found</p>
              <p className="text-lg font-semibold tabular" data-testid="store-best-price">{p.best_price ? formatPrice(p.best_price, currency) : '—'}</p>
            </div>
            <Badge variant="outline" className="rounded-full text-[10px]">{p.offer_count} partner{p.offer_count === 1 ? '' : 's'}</Badge>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function Store() {
  const { currency } = useApp();
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api.storeMeta().then(({ data }) => setMeta(data)).catch(() => {});
    api.storeProducts({}).then(({ data }) => setProducts(data.products)).catch(() => setProducts([]));
  }, []);

  return (
    <Layout>
      <Seo title="Travel Store — Gear, Luggage & Essentials"
        description="Shop creator-recommended travel gear, luggage, cameras and essentials — with prices compared across partners so you get the best price we found. FlightDeals.in Travel Store."
        path="/store"
        jsonLd={breadcrumb([{ name: 'Home', path: '/' }, { name: 'Travel Store' }])} />
      <section className="relative bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-3"><StoreIcon className="h-3.5 w-3.5 text-accent" /> New · Travel Store</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-2"><ShoppingBag className="h-8 w-8 text-accent" /> Travel Store</h1>
          <p className="text-primary-foreground/80 mt-1 max-w-2xl">Creator gear, luggage & travel essentials — compared across partners so you get the best price we found.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>
        {!products ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div>
        ) : (
          <FilteredResults schema={SCHEMAS.store} items={products} testId="store" countNoun="product"
            gridClassName="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            onInteract={() => api.storeTrack('filter_used', {})}
            renderItem={(p) => <ProductCard key={p.id} p={p} currency={currency} />} />
        )}
        <p className="mt-6 text-xs text-muted-foreground flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5" /> “Best price we found” compares only the partners we track — not an exhaustive market comparison. Prices may change on the partner site.</p>
      </section>
    </Layout>
  );
}
